import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { ArchetypeWithSubarchetypes } from "@liga/shared";
import { api, type AdminDeck } from "../api/client";
import { ErrorBox, Loading } from "./Loading";

type SortKey = "usage" | "alpha";

/**
 * Directory of every deck name in the catalogue. Classification now lives
 * on each `event_result` (see `008_drop_deck_subarchetype.sql`), so this
 * panel is purely a directory: it lists each deck and links to
 * `/theadmin/decks/:id/decklists`, where the admin picks a subarchetype
 * per list. The MeleeImportPanel still imports `SubarchetypeSelect` from
 * this module.
 */
export function DecksAdminPanel() {
  const decksQuery = useQuery<AdminDeck[]>({
    queryKey: ["admin", "decks"],
    queryFn: api.adminDecks,
  });

  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("usage");

  const decks = decksQuery.data ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? decks.filter((d) => d.name.toLowerCase().includes(q))
      : decks;
    const sorted = [...list];
    if (sortKey === "alpha") {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    } else {
      sorted.sort(
        (a, b) => b.useCount - a.useCount || a.name.localeCompare(b.name),
      );
    }
    return sorted;
  }, [decks, search, sortKey]);

  return (
    <section className="rounded-2xl border border-line bg-white p-6 space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#8a8170] font-bold mb-1.5">
            Decks
          </div>
          <h2 className="font-display text-xl font-bold text-ink">
            Catálogo de decks
          </h2>
          <p className="text-[14px] leading-relaxed text-[#6b6356] mt-1">
            Cada deck pode ser usado por jogadores diferentes em listas com
            classificações diferentes, então o subarquétipo agora vive em
            cada lista (event_result). Clique no nome do deck para abrir as
            listas e atribuir o subarquétipo de cada uma.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar deck..."
            className="w-72 max-w-full rounded border border-slate-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            aria-label="Ordenar decks"
            className="rounded border border-slate-300 bg-white px-2 py-1.5 text-sm"
          >
            <option value="usage">Mais usados</option>
            <option value="alpha">A → Z</option>
          </select>
        </div>
      </header>

      {decksQuery.isLoading ? (
        <Loading label="Carregando decks..." />
      ) : decksQuery.error ? (
        <ErrorBox error={decksQuery.error} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-50 text-brand-900">
              <tr className="text-[11px] uppercase tracking-wide">
                <th className="text-left px-3 py-2 font-bold">Deck</th>
                <th className="text-right px-3 py-2 w-24 font-bold">Usos</th>
                <th className="text-right px-3 py-2 w-44 font-bold">Listas</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((deck) => (
                <DeckRow key={deck.id} deck={deck} />
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-center text-slate-500 py-8">
                    Nenhum deck corresponde à busca.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function DeckRow({ deck }: { deck: AdminDeck }) {
  return (
    <tr className="border-t border-[#f3efe6] hover:bg-[#fbfaf6] transition-colors">
      <td className="px-3 py-2.5 font-medium text-ink">{deck.name}</td>
      <td className="px-3 py-2.5 text-right tabular-nums text-[#6b6356]">
        {deck.useCount}
      </td>
      <td className="px-3 py-2.5 text-right">
        <Link
          to={`/theadmin/decks/${deck.id}/decklists`}
          className="text-brand-700 font-semibold hover:underline decoration-dotted underline-offset-2 text-sm"
        >
          Abrir listas ↗
        </Link>
      </td>
    </tr>
  );
}

interface SubarchetypeSelectProps {
  archetypes: ArchetypeWithSubarchetypes[];
  value: number | null;
  onChange: (next: number | null) => void;
  disabled?: boolean;
  ariaLabel?: string;
  /**
   * Label for the empty option (`value === null`). Defaults to
   * "— Sem classificação —". The Melee import flow uses a different label
   * ("— Sem subarquétipo —") to match the surrounding copy.
   */
  emptyOptionLabel?: string;
}

/**
 * Native <select> with one <optgroup> per Archetype. Reused by the Melee
 * import flow and by the per-event_result classifier so admins see the
 * same grouped list everywhere.
 */
export function SubarchetypeSelect({
  archetypes,
  value,
  onChange,
  disabled,
  ariaLabel,
  emptyOptionLabel,
}: SubarchetypeSelectProps) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v === "" ? null : Number(v));
      }}
      disabled={disabled}
      aria-label={ariaLabel ?? "Subarquétipo"}
      className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50"
    >
      <option value="">{emptyOptionLabel ?? "— Sem classificação —"}</option>
      {archetypes.map((arch) => (
        <optgroup key={arch.id} label={arch.name}>
          {arch.subarchetypes.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}
